/**
 * Alert store subscriptions and scope labels shared by Insights and Ask.
 */
import { useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";

import { type ChatScopeFilters } from "../api";
import type { FilterOption } from "../components/FilterMultiSelect";
import type { AlertRunProgress } from "./api";
import { alerts_snapshot, run_progress_snapshot, subscribe_alerts } from "./api";
import type { Alert, Schedule, WatchlistScope } from "./types";
import { hasProcessSelection } from "./types";

/** Every saved alert, re-rendering its readers on any mutation. */
export const useAlerts = (): Alert[] => useSyncExternalStore(subscribe_alerts, alerts_snapshot);

/** Live progress of this alert's on-request run, `null` while none is showing. */
export const useRunProgress = (alertId: string): AlertRunProgress | null =>
  useSyncExternalStore(subscribe_alerts, () => run_progress_snapshot().get(alertId) ?? null);

/**
 * The sidebar badge mirrors the unread row badges exactly. Counting the alerts
 * themselves rather than visits makes it decrement as each result is opened.
 */
export const useWatchlistUnreadCount = (): number =>
  useAlerts().filter(alert => alert.unread).length;

/**
 * Slug or id → display name. The scope stores ids because they are stable; the
 * endpoint filters processes by name and the scope label is read by a person,
 * so both want the label. An id with no option left (a deleted process, a user
 * that fell out of visibility) falls through as-is rather than vanishing.
 */
const label_of = (options: FilterOption[], id: string): string =>
  options.find(option => option.id === id)?.label ?? id;

/**
 * The scope written out the way the pills read it — "Marco Rossi · Invoice
 * approval · Last 7 days · Runs every 7 days".
 *
 * What the review endpoint is given instead of the scope itself: it reasons
 * about the scope in prose, and the browser is the only side that already
 * holds the names behind the ids.
 */
export const useScopeSummary = (
  userOptions: FilterOption[],
  processOptions: FilterOption[],
): ((scope: WatchlistScope, schedule: Schedule | null) => string) => {
  const { t } = useTranslation();
  return (scope, schedule) =>
    [
      scope.userIds.length === 0
        ? t("watchlist.select_people")
        : userOptions.length > 0 &&
            scope.userIds.length === userOptions.length &&
            userOptions.every(user => scope.userIds.includes(user.id))
          ? t("watchlist.all_people")
          : scope.userIds.length === 1
            ? label_of(userOptions, scope.userIds[0])
            : t("dashboard.people_selected", { count: scope.userIds.length }),
      scope.processSlugs.length > 0
        ? scope.processSlugs.map(slug => label_of(processOptions, slug)).join(", ")
        : t(hasProcessSelection(scope) ? "watchlist.all_processes" : "watchlist.select_processes"),
      t("watchlist.window_last_n_days", { count: scope.window.days }),
      ...(schedule ? [t("watchlist.runs_every_n_days_count", { count: schedule.everyDays })] : []),
    ].join(" · ");
};

/** Local midnight of a timestamp, which is where the window's borders sit. */
const start_of_day = (value: string): number => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

/**
 * Filters that describe a whole watchlist scope. Every field is optional on the
 * wire — the endpoint serves surfaces that send only some of them — but `send`
 * below always writes these three, and without them there is no scope to
 * reconstruct and so no alert to make.
 */
export type ScopedFilters = ChatScopeFilters & { from: string; to: string; userIds: string[] };

export const hasScope = (filters: ChatScopeFilters): filters is ScopedFilters =>
  typeof filters.from === "string" &&
  typeof filters.to === "string" &&
  Array.isArray(filters.userIds);

/**
 * The inverse of what `send` below writes: the scope a turn was asked under,
 * read back off the filters it carries.
 *
 * Promoting an answer to an alert has to watch the scope that produced *that*
 * answer, which is not necessarily what the composer's chips say by then — the
 * chips can have moved since, which is exactly what a scope note in the
 * transcript marks. A process whose option has gone falls back to its own name,
 * the same way `label_of` falls back to an id.
 */
export const scopeOfFilters = (
  filters: ScopedFilters,
  processOptions: FilterOption[],
): WatchlistScope => ({
  userIds: filters.userIds,
  processSlugs: (filters.processes ?? []).map(
    name => processOptions.find(option => option.label === name)?.id ?? name,
  ),
  // Both ends are inclusive calendar days, so the difference is one short.
  window: {
    days: Math.round((start_of_day(filters.to) - start_of_day(filters.from)) / 86_400_000) + 1,
  },
});
